import { useCallback, useEffect, useRef, useState } from "react";

import { BTLocalStorage } from "types/btStorage";

// eslint-disable-next-line no-restricted-imports
import { getMobileOS } from "utilities/userAgent/userAgent";

import {
    closeDropdowns,
    isResponsiveAutoCollapsed,
    isUserPreferenceCollapsed,
    popoverSelector,
} from "commonComponents/utilities/JobPicker/JobPicker.utils";

const minSidebarWidth = 310;
const maxSidebarWidth = 450;
// Matches @screen-xs in the ant theme. Below this, an open drawer takes the full viewport.
const fullScreenDrawerBreakpoint = 480;

interface IUseJobPickerDrawerParams {
    collapsed: boolean;
    setCollapsed: (isCollapsing: boolean) => void;
    initialWidth: number;
    onResizeWindow?: (collapsed: boolean) => void;
}

export interface IJobPickerDrawerState {
    width: number;
    viewportWidth: number;
    drawerOpen: boolean;
    initialized: boolean;
    useFullWidth: boolean;
    isMobile: boolean;
    handleResize: (x: number) => void;
    handleToggleCollapse: () => void;
    handleDrawerOpen: (e: React.MouseEvent) => void;
    startDrawerClose: () => void;
}

export function useJobPickerDrawer({
    collapsed,
    setCollapsed,
    initialWidth,
    onResizeWindow,
}: IUseJobPickerDrawerParams): IJobPickerDrawerState {
    const [initialized, setInitialized] = useState(false);
    const [width, setWidth] = useState(() =>
        Math.min(Math.max(initialWidth, minSidebarWidth), maxSidebarWidth),
    );
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

    const closeTimeout = useRef<number | null>(null);
    const collapseTimeout = useRef<number | null>(null);

    // If possible, use media queries in CSS to define mobile-specific behavior instead of this
    const mobileOS = getMobileOS();
    const isMobile = mobileOS === "Android" || mobileOS === "iOS";

    // On xs-width screens, the open drawer takes the full viewport so the
    // picker is usable without pinching.
    const useFullWidth = viewportWidth <= fullScreenDrawerBreakpoint && collapsed && drawerOpen;

    // This hides the closed component during initial load to prevent glitchy animation (duration 0.2s)
    useEffect(() => {
        if (!collapsed) {
            setInitialized(true);
            return;
        }
        const t = setTimeout(() => setInitialized(true), 200);
        return () => clearTimeout(t);
    }, [collapsed]);

    // Update CSS custom property for banner positioning
    useEffect(() => {
        const collapsedWidth = drawerOpen ? (useFullWidth ? viewportWidth : width + 5) : 0;
        const actualWidth = collapsed ? collapsedWidth : width;
        document.documentElement.style.setProperty("--job-picker-width", `${actualWidth}px`);

        return () => {
            document.documentElement.style.removeProperty("--job-picker-width");
        };
    }, [collapsed, drawerOpen, width, useFullWidth, viewportWidth]);

    useEffect(() => {
        if (mobileOS === "iOS") {
            return undefined;
        }
        window.addEventListener("scroll", closeDropdowns);
        return () => {
            window.removeEventListener("scroll", closeDropdowns);
        };
    }, [mobileOS]);

    const handleCollapse = useCallback(
        (newState: boolean) => {
            if (!newState && drawerOpen) {
                // Soft drawer is turning into a normal expanded state, delay the transition to hide any flashing
                collapseTimeout.current = setTimeout(() => {
                    setDrawerOpen(false);
                    setCollapsed(newState);
                }, 200);
            } else {
                setCollapsed(newState);
            }
        },
        [drawerOpen, setCollapsed],
    );

    const handleToggleCollapse = useCallback(() => {
        // When the page is too small, instead of expanding the jobpicker open it over the top of the page
        if (isResponsiveAutoCollapsed()) {
            // Fully expanded at a narrow width (no resize event has auto-collapsed it yet) - collapse
            // it, otherwise there is no drawer to toggle and the caret does nothing
            if (!collapsed) {
                setDrawerOpen(false);
                handleCollapse(true);
                return;
            }

            setDrawerOpen(!drawerOpen);

            return;
        }

        // only save the preference when the user is performing the action
        BTLocalStorage.set("bt-string-jobPickerPreference", collapsed ? "open" : "closed");

        handleCollapse(!collapsed);
    }, [drawerOpen, collapsed, handleCollapse]);

    useEffect(() => {
        return () => {
            if (collapseTimeout.current) {
                clearTimeout(collapseTimeout.current);
            }
            if (closeTimeout.current) {
                clearTimeout(closeTimeout.current);
            }
        };
    }, []);

    useEffect(() => {
        const resizeListener = (): void => {
            setViewportWidth(window.innerWidth);
            let isCollapsed = collapsed;
            if (!collapsed && isResponsiveAutoCollapsed()) {
                isCollapsed = true;
                handleCollapse(isCollapsed);
            } else if (collapsed && !isUserPreferenceCollapsed() && !isResponsiveAutoCollapsed()) {
                isCollapsed = false;
                handleCollapse(false);
            }
            if (onResizeWindow) {
                onResizeWindow(isCollapsed);
            }
        };

        window.addEventListener("resize", resizeListener, { passive: true });
        return () => window.removeEventListener("resize", resizeListener);
    }, [collapsed, handleCollapse, onResizeWindow]);

    const handleResize = useCallback((x: number) => {
        const upperBound = Math.min(maxSidebarWidth, window.innerWidth / 2);
        const clampedWidth = Math.min(Math.max(x, minSidebarWidth), upperBound);
        setWidth(clampedWidth);
        BTLocalStorage.set("bt-number-sidebarWidth", clampedWidth);
        window.dispatchEvent(new CustomEvent("resize"));
    }, []);

    const handleDrawerOpen = useCallback(
        (e: React.MouseEvent) => {
            if (closeTimeout.current !== null) {
                clearTimeout(closeTimeout.current);
            }

            if (collapsed && !isMobile && !drawerOpen) {
                // To make this more responsive and work with any adjustment to the main nav height
                // we grab the element and check against the sidebar element position and window scroll position instead of
                // hardcoded navHeight and page position.
                // Todo: once we are fully SPA look into doing this with just CSS on the elements in use.
                const elPos = e.currentTarget.getBoundingClientRect();
                const hoverHitbox = elPos.top + window.scrollY + 100;
                if (e.clientY > hoverHitbox) {
                    setDrawerOpen(true);
                }
            }
        },
        [collapsed, isMobile, drawerOpen],
    );

    const handleDrawerClose = useCallback(() => {
        if (collapsed && drawerOpen) {
            setDrawerOpen(false);
            closeDropdowns();
        }
    }, [collapsed, drawerOpen]);

    const startDrawerClose = useCallback(() => {
        // We DO NOT want to call handleDrawerClose() on Mobile Full Site
        if (!isMobile && collapsed && drawerOpen) {
            if (document.querySelectorAll(popoverSelector).length > 0) {
                // Allow a second for the user to move the mouse back into the soft drawer when closing popups
                closeTimeout.current = setTimeout(handleDrawerClose, 1000);
            } else {
                handleDrawerClose();
            }
        }
    }, [collapsed, drawerOpen, isMobile, handleDrawerClose]);

    return {
        width,
        viewportWidth,
        drawerOpen,
        initialized,
        useFullWidth,
        isMobile,
        handleResize,
        handleToggleCollapse,
        handleDrawerOpen,
        startDrawerClose,
    };
}
